import React, { useState } from "react";
import { Link } from "react-router-dom";
import { RouterPath } from "../../../assets/dictionary/RouterPath";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../../../components/ui/card";
import { Alert, AlertDescription } from "../../../components/ui/alert";
import { Mail, AlertCircle, ArrowLeft } from "lucide-react";
import ForgotPasswordFormService from "./ForgotPasswordFormService";
import { trackEvent } from "../../../lib/matomo";

export default function ForgotPasswordFormModern() {
  const [email, setEmail] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError("");

    try {
      const response = await ForgotPasswordFormService.postSendEmailForgotPassword(email.toLowerCase());
      if (response.status === 200 || response.status === 201) {
        setSuccess(true);
        try { trackEvent("auth", "forgot_password_sent"); } catch(_) {}
      } else {
        setError("Erreur lors de l'envoi de l'email");
        try { trackEvent("auth", "forgot_password_error"); } catch(_) {}
      }
    } catch (err) {
      setError(err.response?.data?.message || "Erreur lors de l'envoi de l'email");
      try { trackEvent("auth", "forgot_password_exception"); } catch(_) {}
    } finally {
      setIsLoading(false);
    }
  };

  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 app-page-gradient">
        <div className="w-full max-w-md">
          <Card className="glass-card border-muted shadow-2xl">
            <CardHeader className="text-center space-y-4">
              <div className="w-16 h-16 bg-green-100 dark:bg-green-900 rounded-full flex items-center justify-center mx-auto">
                <Mail className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              
              <CardTitle className="text-2xl font-bold text-high-contrast">
                Email envoyé !
              </CardTitle>
              
              <CardDescription className="text-muted-foreground">
                Nous avons envoyé un email de réinitialisation à {email}. 
                Vérifiez votre boîte de réception et suivez les instructions.
              </CardDescription>
            </CardHeader>
            
            <CardContent className="space-y-4">
              <Button asChild className="w-full">
                <Link to={RouterPath.LOGIN}>
                  <ArrowLeft className="h-4 w-4 mr-2" />
                  Retour à la connexion
                </Link>
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center p-4 app-page-gradient">
      <div className="w-full max-w-md">
        <Card className="glass-card border-muted shadow-2xl">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl font-bold text-high-contrast">
              Mot de passe oublié
            </CardTitle>
            <CardDescription className="text-muted-foreground">
              Entrez votre adresse email et nous vous enverrons un lien 
              pour réinitialiser votre mot de passe.
            </CardDescription>
          </CardHeader>
          
          <CardContent className="space-y-4">
            {error && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{error}</AlertDescription>
              </Alert>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email" className="text-high-contrast">
                  Email
                </Label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    placeholder="votre@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="pl-10"
                  />
                </div>
              </div>

              <Button
                type="submit"
                className="w-full"
                disabled={isLoading}
              >
                {isLoading ? "Envoi en cours..." : "Envoyer le lien de réinitialisation"}
              </Button>
            </form>

            <div className="text-center">
              <div className="text-sm text-muted-foreground">
                Vous vous souvenez de votre mot de passe ?{" "}
                <Link
                  to={RouterPath.LOGIN}
                  className="text-primary hover:underline font-medium"
                >
                  Se connecter
                </Link>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
} 